import { mulberry32 } from '../math/rng.js';

/**
 * @file THE CAR SPEC: a plain, JSON-able description of one vehicle, drawn from
 * a seed. Nothing in here imports three.js and nothing in here allocates a
 * geometry — `carBuild.js` turns a spec into parts, `CarFleet.js` draws them.
 *
 * Ported from `procgen_cars/vehicle-gen.js` (`makeSpec` + `RANGES` + `PAINTS`),
 * with three changes and no others:
 *
 *  1. the rng is the engine's `math/rng.js#mulberry32` (rule 4 — one generator
 *     for the whole project). It is the same algorithm; the only difference is
 *     that the original coerced seed 0 to 1, which we do not need because
 *     archetype keys always carry a real seed;
 *  2. AUTHORED specials — {@link specPolice} / {@link specSwat} — sit beside the
 *     random roll, because law enforcement is content, not a dice throw;
 *  3. `livery`, `lightbar` and `variant` were ADDED to the spec. They are the
 *     only fields the builder reads that the original had no concept of, and
 *     they are what makes a police car bypass the per-instance paint tint (see
 *     the PAINT MASK section of `carBuild.js`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CONVENTIONS (shared with the whole vehicle stack)
 * ─────────────────────────────────────────────────────────────────────────────
 *  • Real-world METRES. A sedan is 4.5–5.0 m long.
 *  • **+X is the nose**, +Y is up, and **−Z is the driver/left side**. Every
 *    `sd` in the builder is −1 for left and +1 for right, exactly as upstream.
 *  • A spec is data: structured-clone-safe, diffable, and stable for a seed.
 *
 * @module engine/vehicles/carSpec
 */

/** @typedef {import('../types.js').Rng} Rng */

/**
 * The six body types. Order is load-bearing: `makeSpec(seed, 'random')` picks
 * from this array, so appending a type is safe and re-ordering is not.
 * @type {ReadonlyArray<string>}
 */
export const BODY_TYPES = Object.freeze([
  'sedan', 'wagon', 'hatchback', 'suv', 'van', 'pickup',
]);

/**
 * Stock paints, in sRGB hex. Fed to the per-instance tint (NOT to the bake) —
 * `carBuild` bakes painted panels WHITE so one archetype can be recoloured per
 * car without a second bake. Convert with `levels/shared/palette.js#hexToLinear`
 * at the call site.
 * @type {ReadonlyArray<string>}
 */
export const PAINTS = Object.freeze([
  '#8f1f1f', '#16263f', '#1c5f9e', '#e8e4da', '#9aa1a9',
  '#2c5231', '#c19a2e', '#1b1b1e', '#c96a2b', '#513a5e',
]);

/** Cabin trim colours. Only the door cards and the cabin floor survive the prune. */
const INTERIORS = Object.freeze(['#c9b795', '#b3a17e', '#97876c', '#57514a']);

/**
 * Max front-wheel steer angle, radians. SHARED and load-bearing: the rig turns
 * the wheels by it and the body sizes the front arch to clear a tire swept to
 * exactly this angle (`carLayout#archRF`). Changing it without re-deriving the
 * arch puts tread through the fender.
 */
export const STEER_MAX = 0.5;

/** Police livery base (the paint under the white doors/roof/hood). */
export const POLICE_PAINT = '#1b1b1e';
/** SWAT van base — a shade warmer than police black so the two read apart. */
export const SWAT_PAINT = '#23282c';
/** Police/SWAT livery panels. Two-tone: white doors, roof and hood. */
export const LIVERY_WHITE = '#e8e4da';

/**
 * Per-type dimension ranges, verbatim from the source generator. Every entry is
 * `[min, max]` metres unless noted.
 *
 * `deck` (sedan boot), `cargo` (wagon/hatch/suv/van load bay) and `rg` (sedan
 * backlight run) are OPTIONAL: a type that omits one gets 0, which is how the
 * body-shell profile switches between a three-box, a two-box and a bed.
 */
export const RANGES = Object.freeze({
  sedan:     { L: [4.50, 4.95], W: [1.72, 1.86], g: [0.26, 0.31], wheelR: [0.31, 0.35], belt: [0.98, 1.06], rise: [0.40, 0.48], hood: [0.95, 1.15], deck: [0.85, 1.05], ws: [0.58, 0.75], rg: [0.45, 0.62], overF: [0.80, 0.92], overR: [0.85, 1.00] },
  wagon:     { L: [4.60, 5.05], W: [1.72, 1.86], g: [0.27, 0.32], wheelR: [0.32, 0.36], belt: [0.98, 1.06], rise: [0.42, 0.50], hood: [0.95, 1.10], cargo: [1.05, 1.35], ws: [0.55, 0.70], overF: [0.80, 0.92], overR: [0.85, 1.00] },
  hatchback: { L: [3.70, 4.15], W: [1.62, 1.75], g: [0.25, 0.30], wheelR: [0.29, 0.33], belt: [0.94, 1.00], rise: [0.44, 0.52], hood: [0.72, 0.86], cargo: [0.50, 0.65], ws: [0.50, 0.62], overF: [0.62, 0.75], overR: [0.55, 0.68] },
  suv:       { L: [4.35, 4.90], W: [1.80, 1.92], g: [0.38, 0.46], wheelR: [0.36, 0.41], belt: [1.08, 1.18], rise: [0.50, 0.58], hood: [0.85, 1.00], cargo: [0.90, 1.20], ws: [0.50, 0.64], overF: [0.78, 0.90], overR: [0.80, 0.95] },
  van:       { L: [4.40, 4.95], W: [1.78, 1.90], g: [0.30, 0.36], wheelR: [0.32, 0.36], belt: [1.00, 1.10], rise: [0.66, 0.80], hood: [0.45, 0.60], cargo: [1.10, 1.50], ws: [0.55, 0.68], overF: [0.55, 0.68], overR: [0.70, 0.85] },
  pickup:    { L: [4.90, 5.45], W: [1.80, 1.92], g: [0.40, 0.48], wheelR: [0.37, 0.42], belt: [1.04, 1.14], rise: [0.44, 0.52], hood: [1.00, 1.15], ws: [0.50, 0.62], overF: [0.80, 0.92], overR: [0.60, 0.75] },
});

/**
 * @typedef {Object} CarSpec
 * @property {number} seed
 * @property {string} type One of {@link BODY_TYPES}.
 * @property {number} L Overall length, metres (nose at +L/2).
 * @property {number} W Overall width.
 * @property {number} g Ground clearance (the body's lower edge).
 * @property {number} wheelR Tyre radius.
 * @property {number} wheelW Tyre width.
 * @property {number} beltY Beltline height (bottom of the side glass).
 * @property {number} roofY Roof height.
 * @property {number} floorY Cabin floor height.
 * @property {number} hoodLen @property {number} hoodDrop
 * @property {number} wsRun Windscreen run (fore-aft).
 * @property {number} overF Front overhang @property {number} overR Rear overhang.
 * @property {number} deckLen Sedan boot length (0 otherwise).
 * @property {number} cargoLen Load-bay length (0 for sedan/pickup).
 * @property {number} rgRun Sedan backlight run (0 otherwise).
 * @property {number} gateRun Hatch gate run.
 * @property {number} rows 1 or 2 seat rows — 2 rows means FOUR doors.
 * @property {number} spokes Wheel spoke count, 5..8.
 * @property {string} paint sRGB hex.
 * @property {string} interior sRGB hex.
 * @property {{rack: boolean, quadLights: boolean, grilleChrome: boolean,
 *   antenna: boolean, rocker: boolean, doorTrim: boolean, spare: boolean,
 *   cargoStuff: boolean, roofBox: boolean}} x Cosmetic switches.
 * @property {'police'|'swat'|null} [livery] When set, painted panels bake their
 *   LITERAL livery colour with `aPaint = 0` — i.e. the per-instance tint cannot
 *   repaint this car. See `carBuild.js`.
 * @property {boolean} [lightbar] Roof lightbar (siren channels 4 and 5).
 * @property {string} [variant] Free-form label for tooling / the gallery.
 */

/**
 * Rolls one vehicle from a seed. Deterministic and total: every branch draws the
 * same number of values regardless of the outcome, so the stream stays stable.
 *
 * @param {number} seed
 * @param {string} [type='random'] A {@link BODY_TYPES} entry, or `'random'`.
 * @param {string|null} [paint=null] Forces the paint hex.
 * @returns {CarSpec}
 */
export function makeSpec(seed, type = 'random', paint = null) {
  const R = mulberry32(seed);
  /** @param {number} a @param {number} b */
  const rr = (a, b) => a + (b - a) * R();
  /** @param {ReadonlyArray<any>} arr */
  const pick = (arr) => arr[(R() * arr.length) | 0];
  /** @param {number} p */
  const chance = (p) => R() < p;

  const t = type === 'random' ? pick(BODY_TYPES) : type;
  const P = RANGES[t];
  if (!P) throw new Error(`makeSpec: unknown body type "${t}" (expected one of ${BODY_TYPES.join(', ')})`);

  /** @type {any} */
  const s = { seed, type: t };
  s.L = rr(...P.L);
  s.W = rr(...P.W);
  s.g = rr(...P.g);
  s.wheelR = rr(...P.wheelR);
  s.beltY = rr(...P.belt);
  s.roofY = s.beltY + rr(...P.rise);
  s.floorY = s.g + 0.12;
  s.hoodLen = rr(...P.hood);
  s.hoodDrop = rr(0.05, 0.11);
  s.wsRun = rr(...P.ws);
  s.overF = rr(...P.overF);
  s.overR = rr(...P.overR);
  s.deckLen = P.deck ? rr(...P.deck) : 0;
  s.cargoLen = P.cargo ? rr(...P.cargo) : 0;
  s.rgRun = P.rg ? rr(...P.rg) : 0;
  s.gateRun = rr(0.20, 0.32);
  s.rows = (t === 'hatchback') ? (chance(0.6) ? 2 : 1) : (t === 'pickup') ? (chance(0.5) ? 2 : 1) : 2;
  s.wheelW = rr(0.22, 0.26);
  s.spokes = 5 + ((R() * 4) | 0);
  s.paint = paint || pick(PAINTS);
  s.interior = pick(INTERIORS);
  s.x = {
    rack: chance({ wagon: 0.55, suv: 0.42, van: 0.35 }[t] || 0.08),
    quadLights: chance(0.35),
    grilleChrome: chance(0.5),
    antenna: chance(0.6),
    rocker: chance(0.6),
    doorTrim: chance(0.5),
    spare: t === 'suv' && chance(0.45),
    cargoStuff: chance(0.8),
  };
  s.x.roofBox = s.x.rack && chance(0.45);
  s.livery = null;
  s.lightbar = false;
  s.variant = t;
  return /** @type {CarSpec} */ (s);
}

// ─────────────────────────────────────────────────────────────────────────────
// Authored specials
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A police cruiser: a sedan or an SUV, black, no roof rack, with a lightbar.
 *
 * The body-type choice draws from its OWN stream (seed ^ `'POLI'`), never from
 * `makeSpec`'s — otherwise `specPolice(n)` and `makeSpec(n)` would share a
 * generator and adding a coin flip here would reshuffle every civilian car
 * built from the same seed (rule 4, per-stage streams).
 *
 * @param {number} seed
 * @returns {CarSpec}
 */
export function specPolice(seed) {
  const R = mulberry32(((seed >>> 0) ^ 0x504f4c49) >>> 0);
  const s = makeSpec(seed, R() < 0.62 ? 'sedan' : 'suv', POLICE_PAINT);
  // A cruiser is a working vehicle: no rack, no box, no luggage in the boot,
  // and the antenna is not optional.
  s.x.rack = false;
  s.x.roofBox = false;
  s.x.spare = false;
  s.x.cargoStuff = false;
  s.x.antenna = true;
  s.livery = 'police';
  s.lightbar = true;
  s.variant = 'police';
  return s;
}

/**
 * A SWAT van: the van body, near-black, lightbar, nothing on the roof.
 *
 * SWAT carries `livery: 'swat'` rather than `null`, which means its panels bake
 * their literal colour and IGNORE the per-instance tint — a riot van that could
 * be repainted pink by a caller passing a `paint` option is a bug waiting for a
 * screenshot.
 *
 * @param {number} seed
 * @returns {CarSpec}
 */
export function specSwat(seed) {
  const s = makeSpec(seed, 'van', SWAT_PAINT);
  s.x.rack = false;
  s.x.roofBox = false;
  s.x.cargoStuff = false;
  s.x.antenna = true;
  s.livery = 'swat';
  s.lightbar = true;
  s.variant = 'swat';
  return s;
}

/**
 * The livery colour for one paint ZONE. `null` livery means "no livery" — the
 * builder then bakes the panel WHITE and flags it tintable.
 *
 * @param {'police'|'swat'|null|undefined} livery
 * @param {'shell'|'door'|'roof'|'hood'} zone
 * @returns {string|null} sRGB hex, or `null` for "tintable white".
 */
export function liveryColor(livery, zone) {
  if (livery === 'police') return zone === 'shell' ? POLICE_PAINT : LIVERY_WHITE;
  if (livery === 'swat') return SWAT_PAINT;
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Archetype keys
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the ARCHETYPE KEY a `CarFleet` caches on: `"<kind>:<seed>"`, where
 * `kind` is a body type or one of the specials. Every car sharing a key shares
 * one build and one bake, so the key must encode everything that changes
 * geometry — which is exactly (kind, seed), because paint is a per-instance
 * tint and nothing else is variable.
 *
 * @param {string} kind A {@link BODY_TYPES} entry, `'police'`, `'swat'` or `'random'`.
 * @param {number} seed
 * @returns {string}
 */
export function archetypeKey(kind, seed) {
  return `${kind}:${seed >>> 0}`;
}

/**
 * Inverse of {@link archetypeKey}: resolves a key to its spec. This is what
 * lets `CarFleet.addCar('police:7')` work without the caller ever importing a
 * spec factory.
 *
 * @param {string} key
 * @returns {CarSpec}
 */
export function specFromKey(key) {
  const i = String(key).lastIndexOf(':');
  if (i < 0) throw new Error(`specFromKey: "${key}" is not a "<kind>:<seed>" archetype key`);
  const kind = String(key).slice(0, i);
  const seed = Number(String(key).slice(i + 1)) >>> 0;
  if (!Number.isFinite(seed)) throw new Error(`specFromKey: "${key}" has no numeric seed`);
  if (kind === 'police') return specPolice(seed);
  if (kind === 'swat') return specSwat(seed);
  if (kind === 'random' || BODY_TYPES.includes(kind)) return makeSpec(seed, kind);
  throw new Error(`specFromKey: unknown archetype kind "${kind}"`);
}
